import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { PaginatedResponse, CompanySummary } from '@core/types/api';

/** Accept both a bare list and a DRF pagination envelope (see organizationApi). */
function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

/**
 * Company API using RTK Query.
 *
 * `listCompanies` is ORGANIZATION-scoped (the companies of the active tenant), so it lives
 * in the store's `TENANT_SCOPED_APIS` and is reset when the organization changes.
 * It is deliberately NOT company-scoped: the list is what the switcher chooses from,
 * and it is also how a stale `X-Company` gets healed, so it must answer whatever the
 * active company is.
 *
 * The list is the caller's GRANTED companies, not every company of the tenant, so it goes
 * stale the moment an admin narrows somebody's seat. The store invalidates the
 * `Company` tag whenever the active company is dropped, which is what a refused `X-Company`
 * does (`core/api/baseQuery`), so the switcher reloads with the truth.
 */
export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery,
  tagTypes: ['Company'],
  endpoints: (builder) => ({
    listCompanies: builder.query<CompanySummary[], void>({
      query: () => ({
        url: '/api/accounts/companies/',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapList<CompanySummary>(response),
      providesTags: ['Company'],
    }),
  }),
});

export const { useListCompaniesQuery } = companyApi;
